import * as THREE from "./three";
import { BFF_BASE_PATH } from "@/core/utils"

var webgl_debug_event;
var webgl_list_event;

function wgl_debug_update() {
  if (webgl_debug_event != undefined) {
    webgl_debug_event();
  }
}

function webgl_list_update() {
  if (webgl_list_event != undefined) {
    webgl_list_event();
  }
}

// Speed up calls to hasOwnProperty
var hasOwnProperty = Object.prototype.hasOwnProperty;

function isEmpty(obj) {
  // null and undefined are "empty"
  if (obj == null) return true;

  // Assume if it has a length property with a non-zero value
  // that that property is correct.
  if (obj.length > 0) return false;
  if (obj.length === 0) return true;

  // If it isn't an object at this point
  // it is empty, but it can't be anything *but* empty
  // Is it empty?  Depends on your application.
  if (typeof obj !== "object") return true;

  // Otherwise, does it have any properties of its own?
  // Note that this doesn't handle
  // toString and valueOf enumeration bugs in IE < 9
  for (var key in obj) {
    if (hasOwnProperty.call(obj, key)) return false;
  }

  return true;
}

function remove_from_array(array, elem) {
  var index = array.indexOf(elem);
  if (index > -1) {
    array.splice(index, 1);
  }
}

class TilesStack {
  constructor() {
    this.size = 0;
    this.tiles = {};
    this.highest = 0;
  }

  push(tile) {
    this.size++;
    var level = tile.board.level;
    if (this.tiles[level] == undefined) {
      this.tiles[level] = [];
    }
    if (level > this.highest) {
      this.highest = level;
    }
    this.tiles[level].push(tile);
  }

  pop(tile) {
    while (this.tiles[this.highest] == undefined) {
      if (this.highest <= 0) {
        return;
      }
      this.highest--;
    }
    var tile = this.tiles[this.highest].pop();
    if (this.tiles[this.highest].length <= 0) {
      delete this.tiles[this.highest];
    }
    this.size--;
    return tile;
  }
}

class NotificationTile {
  constructor(notification_load, idx) {
    this.notification_load = notification_load;
    this.idx = idx;
    // to support real tile interface
    this.board = null;
    this.roi = null;
    this.generation = null;
  }
  notify() {
    this.notification_load.NotifyOnTile(this.idx);
  }
}

export class NotificationLoad {
  constructor(notification) {
    this.notification = notification;
    this.tile_idx = 0;
    this.tiles = new Set();
  }
  CreateNotificationTile() {
    this.tile_idx++;
    this.tiles.add(this.tile_idx);
    return new NotificationTile(this, this.tile_idx);
  }
  NotifyOnTile(tile_idx) {
    this.tiles.delete(tile_idx);
    if (this.tiles.size == 0) this.NotifyOnLoad();
  }
  NotifyOnLoad() {
    if (this.notification != null) this.notification();
    this.notification = null; // notify just once
  }
}

class TimeStatistics {
  constructor() {
    this.processing_images = {};
    this.total_time_msec = 0;
    this.total_loads = 0;
    this.generation_start = {};
    this.generation_finished = {};
    this.collect_and_show_statistics = false;
  }
  on_start_load(img) {
    if (!this.collect_and_show_statistics) return;

    this.processing_images[img.src] = Date.now();
  }
  on_loaded(img) {
    if (!this.collect_and_show_statistics) return;

    var msec = Date.now() - this.processing_images[img.src];
    this.total_time_msec += msec;
    this.total_loads++;
    delete this.processing_images[img.src];

    if (this.total_loads % 100 == 0) {
      this.show_statistics();
    }
  }
  on_error(img) {
    if (!this.collect_and_show_statistics) return;

    delete this.processing_images[img.src];
  }
  on_start_load_generation(gen) {
    if (!this.collect_and_show_statistics) return;

    this.generation_start[gen] = Date.now();
  }
  on_generation_loaded(gen) {
    if (!this.collect_and_show_statistics) return;

    this.generation_finished[gen] = Date.now();
  }
  show_statistics() {
    console.log(
      "Load tiles statistics: %d loads, average loading time %s msec",
      this.total_loads,
      (this.total_time_msec / this.total_loads).toFixed(2)
    );
    var durations = [];
    var total = 0;
    var number = 0;
    for (var gen in this.generation_start) {
      if (gen in this.generation_finished) {
        durations.push(this.generation_finished[gen] - this.generation_start[gen]);
        total += this.generation_finished[gen] - this.generation_start[gen];
        number++;
      }
    }
    durations.sort((a, b) => a - b);
    console.log(
      "Load generations statistics: %d loads, average loading time %s msec, 90%% median %s msec",
      number,
      (total / number).toFixed(2),
      durations[Math.floor(0.9 * number)].toFixed(2)
    );
  }
}

class TileLoader {
  constructor(gl_canvas) {
    this.tiles_stack = new TilesStack();
    this.gl_canvas = gl_canvas;
    this.asked_stack = {};
    this.loading = 0;
    this.loaded_counter = 0;
    this.max_loaded = 300;
    this.remove_oldest = undefined;
    this.time_statistics = new TimeStatistics();
  }

  load_tile(tile) {
    this.tiles_stack.push(tile);
  }

  push_notification(notification) {
    this.tiles_stack.push(notification);
  }

  requestCORSIfNotSameOrigin(img, url) {
    if (new URL(url).origin !== window.location.origin) img.crossOrigin = "";
  }

  load() {
    this.gl_canvas.invalidate();

    if (this.tiles_stack.size <= 0) {
      this.gl_canvas.check_and_remove_extra_tiles();
      return;
    }
    if (this.loading > utils.urlHelper.max_concurrent_requests()) {
      return;
    }
    var tile = this.tiles_stack.pop();
    if (tile instanceof ROI) {
      tile.board.load_roi(tile.roi, tile.generation);
      this.load();
    } else if (tile instanceof NotificationTile) {
      tile.notify();
    } else {
      var image = new Image();
      var This = this;
      image.onload = function () {
        This.time_statistics.on_generation_loaded(tile.generation);
        This.time_statistics.on_loaded(image);

        delete This.asked_stack[tile.id];
        if (tile.deleted) {
          tile.board.asked--;
          wgl_debug_update();
        } else {
          This.loaded_counter++;
          tile.board.image.tiles_counter++;
          //console.log("gen: %d: loading %d (max %d) to (%d,%d), level: %d, image: %s %s", tile.generation, This.loaded_counter, This.max_loaded, tile.col, tile.row, tile.board.level, tile.board.image_name, tile.board.image.region_name);
          tile.set_image(image);
          if (tile.retriesLeft < 4) {
            console.log(
              `image load succeeded after failing. retries left: ${tile.retriesLeft}, src:`,
              image.src
            );
          }
        }
        This.loading--;
        This.load();
      };
      image.onerror = function (event) {
        if (tile.slide_id !== window.current_slide_id) {
          This.loading--;
          return;
        }
        setTimeout(() => {
          This.time_statistics.on_error(image);
          if (tile.retriesLeft > 0) {
            // give it a couple of attempts. if server at particular port is down - let it retry another port.
            console.log(`image load failed. retries left: ${tile.retriesLeft}, src:`, image.src);
            This.get_tile_path(tile, image);
          } else {
            console.log(`image load failed. no retries left, src:`, image.src);
            delete This.asked_stack[tile.id];
            tile.del();
            tile.board.asked--;
            wgl_debug_update();
            This.loading--;
          }
          This.load();
        }, tile.retriesTiming[tile.retriesLeft - 1]);
        tile.on_error();
      };
      this.loading++;
      this.asked_stack[tile.id] = tile;
      this.get_tile_path(tile, image);
      this.time_statistics.on_start_load(image);
      tile.board.asked += 1;
      tile.board.stacked -= 1;
      tile.asked = true;
      wgl_debug_update();
    }
    this.load();
  }

  get_tile_path(tile, image) {
    var url = tile.get_path();
    if (typeof urlHelper !== "undefined") {
      this.requestCORSIfNotSameOrigin(image, url);
    }
    image.src = url;
  }

  stop() {
    while (this.tiles_stack.size > 0) {
      var tile = this.tiles_stack.pop();
      if (tile instanceof Tile) {
        tile.del();
      } else if (tile instanceof NotificationTile) {
        tile.notify();
      }
    }
  }

  stop_asked() {
    var keys = Object.keys(this.asked_stack);
    for (var i = 0; i < keys.length; i++) {
      var tile = this.asked_stack[keys[i]];
      tile.del();
    }
  }
}

class WebGlImage {
  constructor(webGlCanvas, image_name, slide_name, slide_data, heatmap_data, region_name) {
    this.webGlCanvas = webGlCanvas;
    this.heatmap_data = heatmap_data;
    this.name = image_name;
    this.region_name = region_name;
    this.tiles_counter = 0;
    this.boards = [];
    this.group = new THREE.Group();
    var num_of_levels = this.compute_region(slide_data, region_name);
    var dimensions;
    if (region_name == "") {
      dimensions = slide_data.dimensions;
    } else {
      var region_info = slide_data.regions[region_name];
      var box = region_info.box;
      dimensions = [box[2] - box[0], box[3] - box[1]];
    }
    var level_size = [dimensions[0], dimensions[1]];
    for (var level = 0; level < num_of_levels; level++) {
      if (region_name == "") {
        level_size = slide_data.level_dimensions[level];
      } else {
        if (level > 0) {
          level_size[0] /= 2;
          level_size[1] /= 2;
        }
      }
      var empty_board = region_name != "" && level == num_of_levels - 1;
      var board_layer = new BoardLayer(
        this,
        this.group,
        webGlCanvas,
        slide_name.replace("/", "[@slash]").replace("#", "[@pound]"),
        level,
        level_size[0],
        level_size[1],
        1024,
        dimensions,
        empty_board
      );
      this.boards.push(board_layer);
    }
    this.highest_level = this.boards.length - 1;
  }

  compute_region(slide_data, region_name) {
    if (region_name == "") {
      this.region = {
        left: 0,
        top: 0,
        right: 1,
        bottom: 1,
      };
      return slide_data.level_count;
    } else {
      var region_info = slide_data.regions[region_name];
      var box = region_info.box;
      this.region = {
        left: box[0] / slide_data.dimensions[0],
        top: box[1] / slide_data.dimensions[1],
        right: box[2] / slide_data.dimensions[0],
        bottom: box[3] / slide_data.dimensions[1],
      };
      return region_info.start_level + 2;
    }
  }

  x_to_region(x) {
    var a = 1 / (this.region.right - this.region.left);
    var b = -a * this.region.left;
    return a * x + b;
  }

  x_from_region(x) {
    var a = 1 / (this.region.right - this.region.left);
    var b = -a * this.region.left;
    return (x - b) / a;
  }

  y_to_region(y) {
    var a = 1 / (this.region.bottom - this.region.top);
    var b = -a * this.region.top;
    return a * y + b;
  }

  y_from_region(y) {
    var a = 1 / (this.region.bottom - this.region.top);
    var b = -a * this.region.top;
    return (y - b) / a;
  }

  roi_to_region(roi) {
    return {
      left: this.x_to_region(roi.left),
      right: this.x_to_region(roi.right),
      top: this.y_to_region(roi.top),
      bottom: this.y_to_region(roi.bottom),
    };
  }

  compute_level() {
    var width_part = this.webGlCanvas.roi.right - this.webGlCanvas.roi.left;
    width_part /= this.region.right - this.region.left;
    var height_part = this.webGlCanvas.roi.bottom - this.webGlCanvas.roi.top;
    height_part /= this.region.bottom - this.region.top;
    for (var i = this.boards.length - 1; i >= 0; i--) {
      if (
        this.boards[i].width * width_part > this.webGlCanvas.canvas.width &&
        this.boards[i].height * height_part > this.webGlCanvas.canvas.height
      ) {
        return i;
      }
    }
    return 0;
  }

  extend_roi(roi, factor) {
    var center_x = (roi.left + roi.right) * 0.5;
    var center_y = (roi.top + roi.bottom) * 0.5;
    var dx = (roi.right - roi.left) * 0.5 * factor;
    var dy = (roi.bottom - roi.top) * 0.5 * factor;
    var new_roi = {};
    new_roi.left = center_x - dx;
    new_roi.right = center_x + dx;
    new_roi.top = center_y - dy;
    new_roi.bottom = center_y + dy;
    return new_roi;
  }

  load_roi(/*NotificationTile*/ notification) {
    var level = this.compute_level();
    var roi = this.roi_to_region(this.webGlCanvas.roi);
    if (this.boards[level].empty_board) {
      this.load_roi_level(level - 1, this.extend_roi(roi, 1.3), true);
      if (notification != null) {
        notification.board = this.boards[level];
        notification.roi = roi;
        notification.generation = this.webGlCanvas.generation;
        this.webGlCanvas.tile_loader.push_notification(notification);
      }
      this.load_roi_level(level - 1, roi);
      this.show_level(level - 1);
    } else {
      if (level > 0) this.load_roi_level(level - 1, roi);
      this.load_roi_level(level, this.extend_roi(roi, 1.3), true);
      if (notification != null) {
        notification.board = this.boards[level];
        notification.roi = roi;
        notification.generation = this.webGlCanvas.generation;
        this.webGlCanvas.tile_loader.push_notification(notification);
      }
      this.load_roi_level(level, roi);
      this.show_level(level);
    }
  }

  load_roi_level(level, roi, once) {
    //console.log("load_roi_level(%d, (%s-%s, %s-%s)) %s", level, roi.left.toFixed(2), roi.right.toFixed(2), roi.top.toFixed(2), roi.bottom.toFixed(2), this.name);
    for (var i = level; i < this.boards.length; i++) {
      if (this.boards[i].empty_board) {
        continue;
      }
      var needed = true;
      if (i > 0) {
        var size = this.boards[i].get_roi_size(roi);
        var next_size = this.boards[i - 1].get_roi_size(roi);
        // if once (check single level only) and size == 1 (this is a single tile) - load it anyway
        if (once && size == 1) {
        } else if (size >= next_size) {
          needed = false;
        }
      }
      if (needed) {
        //console.log("level %d needed", i);
        this.webGlCanvas.tile_loader.load_tile(
          new ROI(this.boards[i], roi, this.webGlCanvas.generation)
        );
        this.highest_level = i;
      }
      if (once) {
        break;
      }
    }
  }

  show_level(from_level) {
    if (from_level > this.highest_level) {
      from_level = this.highest_level;
    }
    for (var level = 0; level < this.boards.length; level++) {
      this.boards[level].show(level >= from_level);
    }
  }

  remove_generation(generation) {
    var removed = 0;
    for (var i = 0; i < this.boards.length; i++) {
      removed += this.boards[i].remove_generation(generation);
      this.webGlCanvas.remove_if_not_used(this.boards[i].image);
    }
    return removed;
  }

  find_oldest_generation() {
    var oldest_generation = undefined;
    for (var i = 0; i < this.boards.length; i++) {
      var gen = this.boards[i].find_oldest_generation();
      if (oldest_generation == undefined || gen < oldest_generation) oldest_generation = gen;
    }
    return oldest_generation;
  }
}

export class WebGlCanvas {
  constructor(canvas_id) {
    var scene = (this.scene = new THREE.Scene());
    scene.background = new THREE.Color(0xcecece);
    //Create a different scene to hold our buffer objects
    this.bufferScene = new THREE.Scene();
    var camera = (this.camera = new THREE.OrthographicCamera(0, 1, 1, 0, -500, 1000));
    var canvas = (this.canvas = document.getElementById(canvas_id));
    var renderer = (this.renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true }));
    this.invalidatedAt = new Date();

    this.handleResize();
    var This = this;
    var animate = function () {
      /*
            RequestAnimationFrame all the time, this is a standard pattern;
                animation itself does not consume considerable resources.
            But rendering does consume significant CPU resources -
                so do not render, if scene was intact.
                The perfect solution would be to render upon invalidation, and validate upon rendering.
                But putting a new tile into an engine does not have immediate effect,
                so render a while after last invalidation.
            */
      requestAnimationFrame(animate);

      var invalidatedAgeSeconds = (new Date().getTime() - This.invalidatedAt.getTime()) / 1000;
      if (invalidatedAgeSeconds < 2) {
        renderer.render(scene, camera);
      }
    };
    var This = this;
    window.addEventListener("resize", function () {
      This.handleResize();
    });
    this.roi = {
      top: 0,
      left: 0,
      right: 1,
      bottom: 1,
    };
    this.add_roi_object();
    this.update_roi();
    this.is_zoom_mode = true;
    this.is_show_roi = false;
    this.generation = 0;
    this.tile_loader = new TileLoader(this);
    this.tile_loader.remove_oldest = () => {
      this.remove_oldest();
    };
    this.oldest_generation = 0;
    this.images = {};
    this.curr_image = [[], []];
    this.curr_group = [[], []];
    this.image_names = []; // ordered from oldest to newest
    animate();
  }

  invalidate() {
    this.invalidatedAt = new Date();
  }

  load_roi(/*NotificationLoad*/ notification) {
    this.stop_downloading();
    this.generation++;
    this.tile_loader.time_statistics.on_start_load_generation(this.generation);
    for (var layer = 0; layer < 2; layer++) {
      for (var image = 0; image < this.curr_image[layer].length; image++) {
        this.curr_image[layer][image].load_roi(
          notification ? notification.CreateNotificationTile() : null
        );
      }
    }
    this.tile_loader.load();
    this.invalidate();
  }

  check_and_remove_extra_tiles() {
    if (this.tile_loader.loaded_counter > this.tile_loader.max_loaded) {
      if (this.tile_loader.remove_oldest != undefined) {
        this.tile_loader.remove_oldest();
      }
    }
  }

  set_zoom_mode(is_zoom_mode) {
    this.is_zoom_mode = is_zoom_mode;
    this.update_cameras();
  }

  update_cameras() {
    this.update_camera(this.camera, this.is_zoom_mode);
    this.invalidate();
  }

  update_camera(camera, is_zoom_mode) {
    if (is_zoom_mode) {
      var roi = this.roi;
      camera.left = roi.left;
      camera.right = roi.right;
      camera.bottom = 1 - roi.bottom;
      camera.top = 1 - roi.top;
    } else {
      camera.left = 0;
      camera.right = 1;
      camera.bottom = 0;
      camera.top = 1;
    }
    camera.updateProjectionMatrix();
  }

  add_roi_object() {
    //create a blue LineBasicMaterial
    var material = new THREE.LineBasicMaterial({ color: 0x0000ff });
    var geometry = (this.roi_geom = new THREE.Geometry());
    this.roi_points = [
      new THREE.Vector3(),
      new THREE.Vector3(),
      new THREE.Vector3(),
      new THREE.Vector3(),
    ];
    geometry.vertices.push(this.roi_points[0]);
    geometry.vertices.push(this.roi_points[1]);
    geometry.vertices.push(this.roi_points[2]);
    geometry.vertices.push(this.roi_points[3]);
    geometry.vertices.push(this.roi_points[0]);
    this.roi_object = new THREE.Line(geometry, material);
    this.invalidate();
  }

  show_roi(is_show_roi) {
    if (is_show_roi == this.is_show_roi) {
      return;
    }
    this.is_show_roi = is_show_roi;
    if (is_show_roi) {
      this.update_roi();
      this.scene.add(this.roi_object);
    } else {
      this.scene.remove(this.roi_object);
    }
    this.invalidate();
  }

  update_roi() {
    var roi = this.roi;
    this.roi_points[0].x = this.roi_points[1].x = roi.left;
    this.roi_points[2].x = this.roi_points[3].x = roi.right;
    this.roi_points[0].y = this.roi_points[3].y = 1 - roi.top;
    this.roi_points[1].y = this.roi_points[2].y = 1 - roi.bottom;
    this.roi_geom.verticesNeedUpdate = true;
    this.invalidate();
  }

  set_roi(left, top, right, bottom) {
    var roi = this.roi;
    roi.left = left;
    roi.top = top;
    roi.right = right;
    roi.bottom = bottom;
    if (this.is_show_roi) {
      this.update_roi();
    }
    this.update_cameras();
    this.invalidate();
  }

  get_image_name(slide_name, heatmap_data, region_name) {
    var image_name = slide_name;
    if (heatmap_data != false) {
      image_name += "," + heatmap_data.type;
      for (var i = 0; i < heatmap_data.heatmaps.length; i++) {
        image_name += "," + heatmap_data.heatmaps[i];
      }
      if (heatmap_data.type != "jet") {
        for (var i = 0; i < heatmap_data.colors.length; i++) {
          image_name += "," + heatmap_data.colors[i];
        }
      }
      image_name += "," + region_name;
    }
    return image_name;
  }

  remove_layer(layer) {
    for (var i = 0; i < this.curr_image[layer].length; i++) {
      if (this.curr_image[layer][i].tiles_counter <= 0) {
        this.remove_image(this.curr_image[layer][i].name);
      }
    }
    var scene = layer == 0 ? this.scene : this.bufferScene;
    for (var i = 0; i < this.curr_group[layer].length; i++) {
      scene.remove(this.curr_group[layer][i]);
    }
    this.curr_group[layer] = [];
    this.curr_image[layer] = [];
  }

  load_slide(slide_name, slide_data, heatmap_data) {
    //console.log('load_slide:', slide_name, heatmap_data);

    var layer = heatmap_data == null || heatmap_data == false ? 0 : 1;
    this.remove_layer(1);
    if (layer == 0) {
      this.remove_layer(0);
    }
    if (layer == 0) {
      this.load_slide_internal(slide_name, slide_data, heatmap_data, 0, "");
    } else if (heatmap_data != null && heatmap_data.heatmaps.length > 0) {
      var region_names = Object.keys(slide_data.regions);
      for (var i = 0; i < region_names.length; i++) {
        var region_name = region_names[i];
        this.load_slide_internal(slide_name, slide_data, heatmap_data, 1, region_name);
      }
    }
    this.invalidate();
  }

  clear_slide() {
    this.remove_layer(0);
    this.remove_layer(1);
    this.invalidate();
  }

  load_slide_internal(slide_name, slide_data, heatmap_data, layer, region_name) {
    var image_name = this.get_image_name(slide_name, heatmap_data, region_name);
    var image = this.images[image_name];
    if (image == undefined) {
      this.images[image_name] = new WebGlImage(this, image_name, slide_name, slide_data, [], "");
    } else {
      remove_from_array(this.image_names, slide_name);
    }
    this.curr_image[layer].push(this.images[image_name]);
    this.image_names.push(image_name);
    var group = this.images[image_name].group;
    this.curr_group[layer].push(group);
    if (region_name == "") {
      this.scene.add(group);
    } else {
      this.bufferScene.add(group);
    }
    webgl_list_update();
  }

  get_image_names() {
    return this.image_names;
  }

  show_level(layer, from_level) {
    for (var image = 0; image < this.curr_image[layer].length; image++) {
      this.curr_image[layer][image].show_level(from_level);
    }
    this.invalidate();
  }

  load_tile(layer, level, col, row) {
    for (var image = 0; image < this.curr_image[layer].length; image++) {
      this.curr_image[layer][image].boards[level].load_tile(col, row, this.generation);
    }
    this.tile_loader.load();
    this.invalidate();
  }

  remove_tile(layer, level, col, row) {
    for (var image = 0; image < this.curr_image[layer].length; image++) {
      this.curr_image[layer][image].boards[level].remove_tile(col, row);
    }
  }

  handleResize() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.camera.updateProjectionMatrix();
    this.invalidate();
  }

  set_debug_event(callback) {
    webgl_debug_event = callback;
  }

  set_list_event(callback) {
    webgl_list_event = callback;
  }

  stop_downloading() {
    this.tile_loader.stop();
  }

  remove_image(image_name) {
    remove_from_array(this.image_names, image_name);
    delete this.images[image_name];
    webgl_list_update();
  }

  find_oldest_generation() {
    var oldest_generation = undefined;
    for (var i = 0; i < this.image_names.length; i++) {
      var image_name = this.image_names[i];
      var image = this.images[image_name];
      if (image == undefined) continue;
      var gen = image.find_oldest_generation();
      if (oldest_generation == undefined || gen < oldest_generation) oldest_generation = gen;
    }
    return oldest_generation;
  }

  remove_generation(generation) {
    var removed = 0;
    for (var i = 0; i < this.image_names.length; i++) {
      var image_name = this.image_names[i];
      var image = this.images[image_name];
      if (image == undefined) continue;
      removed += image.remove_generation(generation);
    }
    return removed;
  }

  remove_if_not_used(image) {
    if (
      image.tiles_counter <= 0 &&
      this.curr_image[0].indexOf(image) < 0 &&
      this.curr_image[1].indexOf(image) < 0
    ) {
      this.remove_image(image.name);
      return true;
    }
    return false;
  }

  remove_oldest() {
    var generation_to_remove_from = this.find_oldest_generation();
    if (isNaN(generation_to_remove_from)) return;

    while (
      this.tile_loader.loaded_counter > this.tile_loader.max_loaded &&
      generation_to_remove_from < this.generation
    ) {
      var removed = this.remove_generation(generation_to_remove_from);
      generation_to_remove_from++;
    }
  }

  main_generations() {
    var res = {};

    for (var i = 0; i < this.image_names.length; i++) {
      var image_name = this.image_names[i];
      var image = this.images[image_name];
      for (var level = 0; level < image.boards.length; level++) {
        if (isEmpty(image.boards[level].generations)) continue;
        var gen = parseInt(Object.keys(image.boards[level].generations)[0]);
        var main = res[gen];
        if (main == undefined) {
          res[gen] = 1;
        } else {
          res[gen]++;
        }
      }
    }

    return res;
  }
}

class Array2D {
  constructor(width, height) {
    this.width = width;
    this.height = height;
    this.cells = {};
    this.deleted = 0;
  }

  get(x, y) {
    return this.cells[this.get_key(x, y)];
  }

  set(x, y, cell) {
    this.cells[this.get_key(x, y)] = cell;
  }

  remove(x, y) {
    var key = this.get_key(x, y);
    delete this.cells[key];
  }

  get_key(x, y) {
    return x + "." + y;
  }

  clear() {
    while (true) {
      var keys = Object.keys(this.cells);
      if (keys.length == 0) {
        return;
      }
      var first_key = keys[0];
      this.cells[first_key].del();
    }
  }
}

class ROI {
  constructor(board, roi, generation) {
    this.board = board;
    this.roi = roi;
    this.generation = generation;
  }
}

var g_curr_tile_id = 0;

class Tile {
  constructor(board, col, row, generation, roi) {
    this.id = g_curr_tile_id++;
    this.board = board;
    this.col = col;
    this.row = row;
    this.generation = generation;
    this.mesh = null;
    this.asked = false;
    this.deleted = false;
    this.retriesLeft = 4;
    this.retriesTiming = [8000, 4000, 2000, 1000];
    this.slide_id = globals.viewer.image_data?.slide_id;
    this.roi = roi;
    this.port = utils.urlHelper.get_port(row, col, roi);
  }

  get_path() {
    var request_type;
    var suffix;
    request_type = "read_region";
    suffix = "";

    // save port to use it in case of error
    this.port = utils.urlHelper.get_port(this.row, this.col, this.roi);

    var path =
      (typeof utils.urlHelper !== "undefined"
        ? utils.urlHelper.get_url_pref(this.row, this.col, this.roi)
        : "") +
      `${BFF_BASE_PATH}/` +
      request_type +
      "/" +
      this.board.image_name +
      "/" +
      this.board.level +
      "/" +
      parseInt(Math.round(this.col * this.board.full_tile_size[0])) +
      "/" +
      parseInt(Math.round(this.row * this.board.full_tile_size[1])) +
      "/" +
      parseInt(this.board.tile_size) +
      "/" +
      parseInt(this.board.tile_size) +
      suffix;
    return path;
  }

  set_image(image) {
    var texture = new THREE.Texture(image);
    texture.needsUpdate = true;
    var material = new THREE.MeshBasicMaterial({ map: texture });
    var image = this.board.image;
    var left = this.board.tile_width * this.col;
    var top = this.board.tile_height * this.row;
    var right = Math.min(1, left + this.board.tile_width);
    var bottom = Math.min(1, top + this.board.tile_height);
    var fixed_left = image.x_from_region(left);
    var fixed_top = image.y_from_region(top);
    var fixed_right = image.x_from_region(right);
    var fixed_bottom = image.y_from_region(bottom);
    var geometry = new THREE.PlaneGeometry(fixed_right - fixed_left, fixed_bottom - fixed_top);
    utils.set_uv_coord(
      geometry,
      (right - left) / this.board.tile_width,
      (bottom - top) / this.board.tile_height
    );
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.x = (fixed_left + fixed_right) * 0.5;
    this.mesh.position.y = 1 - (fixed_top + fixed_bottom) * 0.5;
    this.mesh.position.z = -100 - this.board.level;
    this.board.group.add(this.mesh);
    this.board.loaded++;
    this.board.asked--;
    wgl_debug_update();
    this.board.add_to_generation(this);
  }

  del() {
    if (this.mesh) {
      this.board.group.remove(this.mesh);
      this.mesh.geometry.dispose();
      this.mesh.material.dispose();
      this.mesh = undefined;
      this.board.loaded--;
    } else if (this.asked) {
      this.deleted = true;
      this.board.tiles.deleted++;
    } else {
      this.board.stacked--;
    }
    this.board.tiles.remove(this.col, this.row);
    this.board.remove_from_generation(this);
    wgl_debug_update();
  }

  on_error() {
    console.log("tile.on_error: port %d", this.port);
    this.retriesLeft--;
    utils.urlHelper.invalidate_server_port(this.port);
  }
}

class BoardLayer {
  constructor(
    image,
    scene,
    gl_canvas,
    image_name,
    level,
    width,
    height,
    tile_size,
    full_size,
    empty_board
  ) {
    this.image = image;
    this.scene = scene;
    this.gl_canvas = gl_canvas;
    this.tile_loader = gl_canvas.tile_loader;
    this.image_name = image_name;
    this.level = level;
    this.width = width;
    this.height = height;
    this.tile_size = tile_size;
    this.full_size = full_size;
    this.empty_board = empty_board;
    this.full_tile_size = [(full_size[0] * tile_size) / width, (full_size[1] * tile_size) / height];
    this.group = new THREE.Group();
    scene.add(this.group);
    this.visible = true;
    this.tile_width = this.tile_size / this.width;
    this.tile_height = this.tile_size / this.height;
    this.tiles = new Array2D(Math.ceil(width / tile_size), Math.ceil(height / tile_size));
    this.stacked = 0;
    this.loaded = 0;
    this.asked = 0;
    this.generations = {};
  }

  add_to_generation(tile) {
    var gen = this.generations[tile.generation];
    if (gen == undefined) {
      this.generations[tile.generation] = gen = {};
    }
    gen[tile.id] = tile;

    wgl_debug_update();
  }

  remove_from_generation(tile) {
    if (tile.generation == undefined) {
      return false;
    }
    var gen = this.generations[tile.generation];
    if (gen == undefined) {
      return false;
    }
    delete gen[tile.id];
    if (Object.keys(gen).length == 0) {
      delete this.generations[tile.generation];
    }

    wgl_debug_update();
    return true;
  }

  show(visible) {
    if (visible == this.visible) {
      return;
    }
    this.visible = visible;
    if (visible) {
      this.scene.add(this.group);
    } else {
      this.scene.remove(this.group);
    }
  }

  get_roi_limits(roi) {
    var fixed_roi = roi; //this.image.roi_to_region(roi);
    var limits = {};

    // check, if we are totally out of region
    if (
      (fixed_roi.left < 0 && fixed_roi.right < 0) ||
      (fixed_roi.left >= 1 && fixed_roi.right >= 1) ||
      (fixed_roi.top < 0 && fixed_roi.bottom < 0) ||
      (fixed_roi.top >= 1 && fixed_roi.bottom >= 1)
    ) {
      limits.from_col = 0;
      limits.to_col = 0;
      limits.from_row = 0;
      limits.to_row = 0;
    } else {
      var left = (fixed_roi.left * this.full_size[0]) / this.full_tile_size[0];
      var right = (fixed_roi.right * this.full_size[0]) / this.full_tile_size[0];
      var top = (fixed_roi.top * this.full_size[1]) / this.full_tile_size[1];
      var bottom = (fixed_roi.bottom * this.full_size[1]) / this.full_tile_size[1];
      limits.from_col = Math.max(0, parseInt(left));
      limits.to_col = Math.max(limits.from_col + 1, Math.min(this.tiles.width, Math.ceil(right)));
      limits.from_row = Math.max(0, parseInt(top));
      limits.to_row = Math.max(limits.from_row + 1, Math.min(this.tiles.height, Math.ceil(bottom)));
    }
    return limits;
  }

  get_roi_size(roi) {
    var limits = this.get_roi_limits(roi);
    return (limits.to_col - limits.from_col) * (limits.to_row - limits.from_row);
  }

  get_visible_size(roi, col, row) {
    var left = this.tile_width * col;
    var top = this.tile_height * row;
    var right = left + this.tile_width;
    var bottom = top + this.tile_height;
    left = Math.max(left, Math.max(0, roi.left));
    top = Math.max(top, Math.max(0, roi.top));
    right = Math.min(right, Math.min(1, roi.right));
    bottom = Math.min(bottom, Math.min(1, roi.bottom));
    var size = (right - left) * (bottom - top);
    // make sure that if tiles have the same size, they will be ordered by rows and than by cols
    return Math.floor(size * 100000) * 1000000 + row * 1000 + col;
  }

  load_roi(roi, generation) {
    var tiles = [];
    var limits = this.get_roi_limits(roi);
    for (var row = limits.from_row; row < limits.to_row; row++) {
      for (var col = limits.from_col; col < limits.to_col; col++) {
        tiles.push({ col: col, row: row, size: this.get_visible_size(roi, col, row) });
      }
    }
    tiles.sort(function (a, b) {
      // sort by visible size
      return a.size - b.size;
    });
    for (var i = 0; i < tiles.length; i++) {
      var tile = tiles[i];
      this.load_tile(tile.col, tile.row, generation, roi);
    }
  }

  load_tile(col, row, generation, roi) {
    var tile = this.tiles.get(col, row);
    if (tile != undefined) {
      this.remove_from_generation(tile);
      tile.generation = generation;
      this.add_to_generation(tile);
      return;
    }
    tile = new Tile(this, col, row, generation, roi);
    this.tiles.set(col, row, tile);
    this.stacked++;
    this.tile_loader.load_tile(tile);
    wgl_debug_update();
  }

  remove_tile(col, row) {
    var tile = this.tiles.get(col, row);
    if (tile == undefined) {
      return false;
    }
    return remove_tile(tile);
  }

  remove_tile(tile) {
    this.tile_loader.loaded_counter--;
    this.image.tiles_counter--;
    tile.del();
    return true;
  }

  get_stat() {
    var generations = {};

    var keys = Object.keys(this.gl_canvas.main_generations());
    for (var i = 0; i < keys.length; i++) {
      var gen = this.generations[keys[i]];
      generations[keys[i]] = gen == undefined ? 0 : Object.keys(gen).length;
    }

    return {
      level: this.level,
      stacked: this.stacked,
      asked: this.asked,
      loaded: this.loaded,
      generations: generations,
    };
  }

  clear() {
    this.tiles.clear();
  }

  find_oldest_generation() {
    var oldest_generation = undefined;
    var keys = Object.keys(this.tiles.cells);
    for (var i = 0; i < keys.length; i++) {
      var tile = this.tiles.cells[keys[i]];
      if (tile == undefined) continue;
      var gen = tile.generation;
      if (oldest_generation == undefined || gen < oldest_generation) oldest_generation = gen;
    }
    return oldest_generation;
  }

  remove_generation(generation) {
    var removed = 0;
    var keys = Object.keys(this.tiles.cells);
    for (var i = 0; i < keys.length; i++) {
      var tile = this.tiles.cells[keys[i]];
      if (tile == undefined) continue;
      if (tile.generation <= generation) {
        if (this.remove_tile(tile)) removed++;
      }
    }
    return removed;
  }
}
